import React, { useContext, useEffect, useMemo, useState } from 'react';
import { EntityValidations, ISpecificEntityFormProps, IApp03Portfolio, App03PortfolioUser, App03Portfolio, CustomField } from '../../types';
import { LookupService } from '../../services';
import styles from '../../styles/cr.module.scss';
import { CrUserPicker } from '../cr/CrUserPicker';
import { CrTextField } from '../cr/CrTextField';
import { CrDropdown } from '../cr/CrDropdown';
import { EntityForm } from '../EntityForm';
import { DataContext } from '../DataContext';
import { IntegrationContext } from '../IntegrationContext';
import { PortfolioTypes } from '../../types/App03Portfolio';
import { App03Frameworks } from '../../types/App03Workflow';
import { getFilteredCustomFieldDfns } from '../../AppGlobals';
import { CrCustomFields } from '../cr/CrCustomFields';

class PortfolioValidations extends EntityValidations {
    public Owners: string = null;
}

interface WFPortfolioFormProps extends ISpecificEntityFormProps {
    frameworkID: App03Frameworks;
}

export const WFPortfolioForm = (props: WFPortfolioFormProps): React.ReactElement => {
    const { disableGroupManagement } = useContext(IntegrationContext);
    const { dataServices, lookupData, loadLookupData: { customFieldDfns, entityStatuses, users: { all: allUsers } } } = useContext(DataContext);

    useMemo(() => entityStatuses(), [entityStatuses]);
    useMemo(() => customFieldDfns(), [customFieldDfns]);
    useMemo(() => allUsers(), [allUsers]);

    const validateEntity = (portfolio: IApp03Portfolio): Promise<PortfolioValidations> => {
        const errors = new PortfolioValidations();

        if (portfolio.Title === null || portfolio.Title === '') {
            errors.Title = `Portfolio name is required`;
            errors.Valid = false;
        }
        else {
            errors.Title = null;
        }
        if (portfolio.App03PortfolioUsers === undefined || portfolio.App03PortfolioUsers === null || portfolio.App03PortfolioUsers.length < 1) {
            errors.Owners = `At least one owner is required`;
            errors.Valid = false;
        }
        else {
            errors.Owners = null;
        }

        return Promise.resolve(errors);
    };

    const filteredCustomFieldDfns = useMemo(() => {
        return getFilteredCustomFieldDfns(
            lookupData.CustomFieldDfns,
            [
                'All Apps - Common',
                'All Delivery Apps - Portfolio',
            ]
        );
    }, [lookupData.CustomFieldDfns]);

    return (
        <EntityForm<IApp03Portfolio, PortfolioValidations>
            {...props}
            entityName='Portfolio'
            renderFormFields={(changeHandlers, formState) => {
                const { FormData: portfolio, ValidationErrors: errors } = formState;
                return (
                    <div>
                        <CrTextField
                            label="Name"
                            maxLength={100}
                            className={styles.formField}
                            required={true}
                            value={portfolio.Title}
                            onChange={(ev, newValue) => changeHandlers.changeTextField(newValue, 'Title')}
                            errorMessage={errors.Title}
                        />

                        <CrUserPicker
                            label="Lead Owner"
                            className={styles.formField}
                            users={lookupData.Users?.All}
                            selectedUsers={portfolio.LeadUserID && [portfolio.LeadUserID]}
                            onChange={u => changeHandlers.changeUserPicker(u, 'LeadUserID')}
                        />

                        <CrUserPicker
                            label="Owner(s)"
                            className={styles.formField}
                            users={lookupData.Users?.All}
                            itemLimit={5}
                            required={true}
                            errorMessage={errors.Owners}
                            selectedUsers={portfolio.App03PortfolioUsers?.map(u => u.UserID)}
                            onChange={v => changeHandlers.changeMultiUserPicker(v, 'App03PortfolioUsers', new App03PortfolioUser(), 'UserID')}
                        />

                        <CrCustomFields
                            label="Additional Information"
                            className={styles.formField}
                            customFieldDfns={filteredCustomFieldDfns}
                            selectedItems={LookupService.customFieldsToDropdownWithText(portfolio.CustomFields)}
                            onChange={v => changeHandlers.changeMultiDropdownWithText(v, 'CustomFields', new CustomField(), 'CustomFieldDefnID', 'CustomFieldValue')}
                        />

                        <CrDropdown
                            label="Status"
                            className={styles.formField}
                            disabled={disableGroupManagement}
                            options={LookupService.entitiesToSelectableOptions(lookupData.EntityStatuses)}
                            selectedKey={portfolio.EntityStatusID}
                            onChange={(_, o) => changeHandlers.changeStatusDropdown(o, 'EntityStatusID')}
                        />

                    </div>
                );
            }}
            loadEntity={id => dataServices.app03PortfolioService.read(id, true, true)}
            loadNewEntity={() => new App03Portfolio(PortfolioTypes.General, props.frameworkID)}
            loadEntityValidations={() => new PortfolioValidations()}
            onValidateEntity={validateEntity}
            onCreate={p => dataServices.app03PortfolioService.create(p)}
            onUpdate={p => dataServices.app03PortfolioService.update(p.ID, p)}
            parentEntities={dataServices.app03PortfolioService.parentEntities}
            childEntities={[
                {
                    ObjectParentProperty: 'App03PortfolioUsers',
                    ParentIdProperty: 'App03PortfolioID',
                    ChildIdProperty: 'UserID',
                    ChildService: dataServices.app03PortfolioUserService
                },
                {
                    ObjectParentProperty: 'CustomFields',
                    ParentIdProperty: 'App03PortfolioID',
                    ChildIdProperty: 'CustomFieldDefnID',
                    ChildService: dataServices.customFieldService
                },
            ]}
        />
    );
};
